import React, { useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { KeyRound, Loader2, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ChangePassword — self-service page for the current user to rotate
 * their own password. Used both as a standalone screen ("Settings → Change
 * password") AND as the forced landing page after a login when the admin
 * flagged the user with must_reset_password on User Master.
 */
const ChangePassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const forced = !!location.state?.forced;

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (next === current) {
      toast.error('New password must differ from the current one');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true },
      );
      toast.success('Password updated. Signed out of other sessions.');
      // On forced-reset flows, the flag is now cleared server-side — send
      // them to the dashboard the same way LoginPage would have.
      const nextScreen = location.state?.user?.default_screen || '/dashboard';
      navigate(nextScreen, { replace: true });
    } catch (err) {
      console.error('Change password failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md" data-testid="change-password-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            Change your password
          </CardTitle>
          {forced && (
            <div
              className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="forced-notice"
            >
              <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Your administrator has required a password reset before you continue.
                Set a new password to access the app.
              </span>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="cp-current">Current password</Label>
              <div className="relative">
                <Input
                  id="cp-current"
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  data-testid="cp-current"
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowCurrent((v) => !v)}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="cp-new">New password</Label>
              <div className="relative">
                <Input
                  id="cp-new"
                  type={showNext ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  data-testid="cp-new"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowNext((v) => !v)}
                >
                  {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="cp-confirm">Confirm new password</Label>
              <Input
                id="cp-confirm"
                type={showNext ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="cp-confirm"
                required
              />
            </div>

            <div className="pt-2 flex gap-2">
              {!forced && (
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                  data-testid="cp-cancel"
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className="flex-1"
                disabled={submitting}
                data-testid="cp-submit"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Update password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;
